import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicalContext } from '../contexts/ClinicalContext';
import { biologicalEntityService } from '../services/firebaseService';
import { useOrg } from '../hooks/useOrg';
import antibioticsList from '../antibiotics-list.json';
import comprehensiveData from '../comprehensive-biological-entities-complete.json';
import BiocideList from './ipc/BiocideList';

const IPCSetup = ({ activeSection = 'ipc-ic-leadership' }) => {
  const { t } = useTranslation('ipc');
  const { activeOrgId, isLoading: orgLoading } = useOrg();
  const { state, actions } = useContext(ClinicalContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAddEntityForm, setShowAddEntityForm] = useState(false);
  const [showEntityDetails, setShowEntityDetails] = useState(false);
  const [biological_entities, setBiologicalEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [editingEntity, setEditingEntity] = useState(null);
  const [saveEntitySuccess, setSaveEntitySuccess] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Load biological entities for active organization only
  useEffect(() => {
    const loadBiologicalEntities = async () => {
      if (orgLoading) return;
      if (!activeOrgId) {
        setBiologicalEntities([]);
        setLoadingEntities(false);
        return;
      }
      try {
        setLoadingEntities(true);
        console.log('🦠 Loading biological entities from Firebase (org-scoped)...');
        const entities = await biologicalEntityService.getWhere(
          'organizationId',
          '==',
          activeOrgId
        );
        console.log(`✅ Loaded ${entities.length} biological entities for org ${activeOrgId}`);
        setBiologicalEntities(entities);
      } catch (error) {
        console.error('❌ Error loading biological entities:', error);
        const fallback = (state.biological_entities || []).filter(
          (e) => e.organizationId === activeOrgId
        );
        setBiologicalEntities(fallback);
      } finally {
        setLoadingEntities(false);
      }
    };

    loadBiologicalEntities();
  }, [activeOrgId, orgLoading]);

  // Handle entity editing
  const handleEditEntity = (entity) => {
    setEditingEntity({ ...entity });
    setEditingEntityId(entity.id);
    setSelectedEntity(entity);
    setShowEntityDetails(false); // Use inline editing instead of modal
  };

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Update nested object fields
  const updateNestedField = (entityId, path, value) => {
    const pathArray = path.split('.');
    setBiologicalEntities(prev => 
      prev.map(entity => {
        if (entity.id === entityId) {
          const newEntity = { ...entity };
          let current = newEntity;
          
          // Navigate to the parent of the target field
          for (let i = 0; i < pathArray.length - 1; i++) {
            if (!current[pathArray[i]]) {
              current[pathArray[i]] = {};
            }
            current = current[pathArray[i]];
          }
          
          // Set the final field
          current[pathArray[pathArray.length - 1]] = value;
          
          return newEntity;
        }
        return entity;
      })
    );
  };

  // Update array fields
  const updateArrayField = (entityId, field, value) => {
    const arrayValue = typeof value === 'string' 
      ? value.split(',').map(item => item.trim()).filter(item => item !== '')
      : value;
    
    setBiologicalEntities(prev => 
      prev.map(entity => {
        if (entity.id === entityId) {
          return { ...entity, [field]: arrayValue };
        }
        return entity;
      })
    );
  };

  // Validation functions from the document
  const validateBiologicalEntity = (entity) => {
    const errors = [];
    const warnings = [];
    
    // Required fields
    if (!entity.scientificName) errors.push("scientificName is required");
    if (!entity.category) errors.push("category is required");
    
    // Risk scoring validation
    if (!entity.severityScore || entity.severityScore < 1 || entity.severityScore > 10) {
      errors.push("severityScore must be between 1-10");
    }
    if (!entity.occurrenceProbability || entity.occurrenceProbability < 1 || entity.occurrenceProbability > 10) {
      errors.push("occurrenceProbability must be between 1-10");
    }
    if (!entity.detectionDifficulty?.score || entity.detectionDifficulty.score < 1 || entity.detectionDifficulty.score > 10) {
      errors.push("detectionDifficulty.score must be between 1-10");
    }
    
    // RPN calculation validation
    if (entity.severityScore && entity.occurrenceProbability && entity.detectionDifficulty?.score) {
      const calculatedRPN = entity.severityScore * entity.occurrenceProbability * entity.detectionDifficulty.score;
      if (entity.rpnScore && Math.abs(entity.rpnScore - calculatedRPN) > 1) {
        errors.push(`RPN score mismatch: expected ${calculatedRPN}, got ${entity.rpnScore}`);
      }
    }
    
    // Enum validations
    const validCategories = ['Bacterial', 'Viral', 'Fungal', 'Parasitic'];
    if (entity.category && !validCategories.includes(entity.category)) {
      errors.push(`Invalid category: ${entity.category}. Must be one of: ${validCategories.join(', ')}`);
    }
    
    const validPriorities = ['Critical', 'High', 'Medium', 'Low'];
    if (entity.priority && !validPriorities.includes(entity.priority)) {
      errors.push(`Invalid priority: ${entity.priority}. Must be one of: ${validPriorities.join(', ')}`);
    }
    
    // Array validations
    if (entity.transmissionRoutes && (!Array.isArray(entity.transmissionRoutes) || entity.transmissionRoutes.length === 0)) {
      warnings.push("transmissionRoutes should be a non-empty array");
    }
    
    if (entity.associatedInfectionTypes && !Array.isArray(entity.associatedInfectionTypes)) {
      errors.push("associatedInfectionTypes must be an array");
    }
    
    // Object structure validations
    if (entity.incubationPeriod) {
      if (typeof entity.incubationPeriod !== 'object') {
        errors.push("incubationPeriod must be an object");
      } else {
        if (entity.incubationPeriod.min && entity.incubationPeriod.max && entity.incubationPeriod.min > entity.incubationPeriod.max) {
          errors.push("incubationPeriod.min cannot be greater than max");
        }
      }
    }
    
    // Warnings for missing recommended fields
    if (!entity.taxonomicFamily || entity.taxonomicFamily === "Unknown - requires review") {
      warnings.push("taxonomicFamily should be reviewed and updated");
    }
    
    if (!entity.treatmentOptions || entity.treatmentOptions.length === 0) {
      warnings.push("treatmentOptions should be populated");
    }
    
    if (!entity.references || entity.references.length === 0) {
      warnings.push("references should include CDC/WHO guidelines");
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  };

  // Auto-calculate RPN score
  const calculateRPN = (severityScore, occurrenceProbability, detectionDifficulty) => {
    if (severityScore && occurrenceProbability && detectionDifficulty) {
      return severityScore * occurrenceProbability * detectionDifficulty;
    }
    return 0;
  };

  // Handle entity field updates
  const updateEditingEntity = (field, value) => {
    setEditingEntity(prev => ({ ...prev, [field]: value }));
  };

  // Handle comma-separated field updates
  const updateCommaSeparatedField = (field, value) => {
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item !== '');
    setEditingEntity(prev => ({ ...prev, [field]: arrayValue }));
  };

  // Helper function for resistance profile multi-select
  const createResistanceProfileDropdown = (resistanceProfile) => {
    // Handle both old (array) and new (object) formats
    const selectedValues = Array.isArray(resistanceProfile) 
      ? resistanceProfile 
      : (resistanceProfile?.patterns || []);
    
    return (
      <div style={{ 
        border: '1px solid #e2e8f0', 
        borderRadius: '6px', 
        padding: '8px',
        maxHeight: '120px',
        overflowY: 'auto',
        backgroundColor: '#ffffff'
      }}>
        {antibioticsList.antibiotics.map(antibiotic => (
          <label key={antibiotic} style={{ 
            display: 'block', 
            marginBottom: '4px',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={selectedValues.includes(antibiotic)}
              onChange={(e) => {
                let newResistanceProfile;
                if (e.target.checked) {
                  const newPatterns = [...selectedValues, antibiotic];
                  // Use new format (object)
                  newResistanceProfile = {
                    patterns: newPatterns,
                    mechanisms: resistanceProfile?.mechanisms || [],
                    concernLevel: resistanceProfile?.concernLevel || 'Medium',
                    emergingThreats: resistanceProfile?.emergingThreats || [],
                    geographicDistribution: resistanceProfile?.geographicDistribution || 'Variable'
                  };
                } else {
                  const newPatterns = selectedValues.filter(item => item !== antibiotic);
                  // Use new format (object)
                  newResistanceProfile = {
                    patterns: newPatterns,
                    mechanisms: resistanceProfile?.mechanisms || [],
                    concernLevel: resistanceProfile?.concernLevel || 'Medium',
                    emergingThreats: resistanceProfile?.emergingThreats || [],
                    geographicDistribution: resistanceProfile?.geographicDistribution || 'Variable'
                  };
                }
                updateEditingEntity('resistanceProfile', newResistanceProfile);
              }}
              style={{ marginRight: '8px' }}
            />
            {antibiotic}
          </label>
        ))}
      </div>
    );
  };

  // Save entity changes
  const saveEntityChanges = async () => {
    if (!editingEntity || !activeOrgId) return;
    if (editingEntity.organizationId && editingEntity.organizationId !== activeOrgId) {
      console.warn('[IPCSetup] Refusing to save entity from another organization');
      return;
    }

    const toSave = {
      ...editingEntity,
      organizationId: editingEntity.organizationId || activeOrgId
    };

    try {
      await biologicalEntityService.update(editingEntity.id, toSave);
      
      // Update local state
      setBiologicalEntities(prev => 
        prev.map(entity => entity.id === toSave.id ? toSave : entity)
      );
      
      setSaveEntitySuccess(true);
      setTimeout(() => setSaveEntitySuccess(false), 2000);
      
      console.log('✅ Entity updated successfully:', editingEntity.scientificName);
    } catch (error) {
      console.error('❌ Error updating entity:', error);
    }
  };

  const [newEntity, setNewEntity] = useState({
    // IDENTIFICATION
    scientificName: '',
    commonName: '',
    synonyms: [],
    
    // CLASSIFICATION
    category: '',
    classification: '',
    taxonomicFamily: '',
    taxonomicOrder: '',
    
    // WHO/CDC ALIGNMENT
    whoPriorityTier: '',
    cdcNotifiable: false,
    cdcCategory: '',
    associatedInfectionTypes: [],
    
    // TRANSMISSION
    transmissionRoutes: [],
    primaryTransmissionRoute: '',
    typicalOnset: '',
    incubationPeriod: {
      min: '',
      max: '',
      unit: 'days',
      typical: '',
      notes: ''
    },
    environmentalSurvival: {
      surfaces: '',
      duration: '',
      conditions: '',
      disinfectionRequired: ''
    },
    infectiousDose: {
      description: '',
      level: ''
    },
    colonizationNotes: '',
    
    // CLINICAL IMPACT
    commonSites: [],
    riskFactors: [],
    susceptiblePopulations: [],
    clinicalPresentation: {
      symptoms: [],
      severityRange: '',
      typicalCourse: '',
      diagnosticCriteria: []
    },
    complications: [],
    mortalityRate: {
      treated: '',
      untreated: '',
      attributable: '',
      context: '',
      source: ''
    },
    
    // RISK SCORING
    severityScore: '',
    occurrenceProbability: '',
    detectionDifficulty: {
      score: '',
      reasons: [],
      timeToResults: '',
      falseNegativeRisk: ''
    },
    rpnScore: '',
    priority: '',
    
    // RESISTANCE & TREATMENT
    resistanceProfile: {
      patterns: [],
      mechanisms: [],
      concernLevel: '',
      emergingThreats: [],
      geographicDistribution: ''
    },
    treatmentOptions: [],
    vaccineAvailable: false,
    vaccineDetails: null,
    
    // PREVENTION & CONTROL
    preventionStrategies: {
      standard: [],
      transmission: [],
      deviceAssociated: [],
      isolation: {
        type: '',
        ppeRequired: [],
        roomRequirements: '',
        duration: '',
        visitorRestrictions: ''
      },
      environmentalControls: {
        cleaningFrequency: '',
        disinfectants: [],
        airHandling: '',
        equipmentReprocessing: ''
      },
      surveillance: {
        screeningRequired: false,
        screeningFrequency: '',
        colonizationScreening: false,
        activeScreeningPopulations: []
      }
    },
    
    // DETECTION
    detectionMethods: [],
    confirmationRequired: true,
    reportingRequirements: {
      localHealthDept: false,
      nationalSurveillance: false,
      timeframe: '',
      methods: []
    },
    
    // REFERENCES & EDUCATION
    references: [],
    educationalResources: [],
    
    // AUDIT TRAIL
    createdBy: '',
    updatedBy: '',
    lastReviewedBy: '',
    lastReviewedDate: '',
    reviewDueDate: '',
    version: 1,
    status: 'Active'
  });

  // Update IPC data
  const updateICLeadership = (field, value) => {
    actions.updateItem('icLeadership', 'icLeadership', { [field]: value });
  };

  const updateRiskManagement = (field, value) => {
    actions.updateItem('riskManagement', 'riskManagement', { [field]: value });
  };

  const updatePolicyFramework = (field, value) => {
    actions.updateItem('policyFramework', 'policyFramework', { [field]: value });
  };

  const updateHAITargets = (field, value) => {
    actions.updateItem('haiTargetRates', 'haiTargetRates', { [field]: value });
  };

  const addEntity = async () => {
    if (!activeOrgId) {
      console.warn('[IPCSetup] Cannot add biological entity without active organization');
      return;
    }
    const entity = {
      id: `entity-${Date.now()}`,
      ...newEntity,
      organizationId: activeOrgId,
      createdAt: new Date().toISOString(),
      createdBy: 'current-user' // TODO: Get from auth context
    };
    
    try {
      // Add to Firebase directly
      await biologicalEntityService.create(entity);
      
      // Update local state
      setBiologicalEntities(prev => [...prev, entity]);
    
    // Reset form
    setNewEntity({
        // IDENTIFICATION
      scientificName: '',
      commonName: '',
        synonyms: [],
        
        // CLASSIFICATION
      category: '',
        classification: '',
        taxonomicFamily: '',
        taxonomicOrder: '',
        
        // WHO/CDC ALIGNMENT
        whoPriorityTier: '',
        cdcNotifiable: false,
        cdcCategory: '',
        associatedInfectionTypes: [],
        
        // TRANSMISSION
        transmissionRoutes: [],
        primaryTransmissionRoute: '',
        typicalOnset: '',
        incubationPeriod: {
          min: '',
          max: '',
          unit: 'days',
          typical: '',
          notes: ''
        },
        environmentalSurvival: {
          surfaces: '',
          duration: '',
          conditions: '',
          disinfectionRequired: ''
        },
        infectiousDose: {
          description: '',
          level: ''
        },
        colonizationNotes: '',
        
        // CLINICAL IMPACT
      commonSites: [],
      riskFactors: [],
        susceptiblePopulations: [],
        clinicalPresentation: {
          symptoms: [],
          severityRange: '',
          typicalCourse: '',
          diagnosticCriteria: []
        },
        complications: [],
        mortalityRate: {
          treated: '',
          untreated: '',
          attributable: '',
          context: '',
          source: ''
        },
        
        // RISK SCORING
        severityScore: '',
        occurrenceProbability: '',
        detectionDifficulty: {
          score: '',
          reasons: [],
          timeToResults: '',
          falseNegativeRisk: ''
        },
        rpnScore: '',
        priority: '',
        
        // RESISTANCE & TREATMENT
        resistanceProfile: {
          patterns: [],
          mechanisms: [],
          concernLevel: '',
          emergingThreats: [],
          geographicDistribution: ''
        },
        treatmentOptions: [],
        vaccineAvailable: false,
        vaccineDetails: null,
        
        // PREVENTION & CONTROL
      preventionStrategies: {
        standard: [],
          transmission: [],
          deviceAssociated: [],
          isolation: {
            type: '',
            ppeRequired: [],
            roomRequirements: '',
            duration: '',
            visitorRestrictions: ''
          },
          environmentalControls: {
            cleaningFrequency: '',
            disinfectants: [],
            airHandling: '',
            equipmentReprocessing: ''
          },
          surveillance: {
            screeningRequired: false,
            screeningFrequency: '',
            colonizationScreening: false,
            activeScreeningPopulations: []
          }
        },
        
        // DETECTION
        detectionMethods: [],
        confirmationRequired: true,
        reportingRequirements: {
          localHealthDept: false,
          nationalSurveillance: false,
          timeframe: '',
          methods: []
        },
        
        // REFERENCES & EDUCATION
        references: [],
        educationalResources: [],
        
        // AUDIT TRAIL
        createdBy: '',
        updatedBy: '',
        lastReviewedBy: '',
        lastReviewedDate: '',
        reviewDueDate: '',
        version: 1,
        status: 'Active'
    });
    
    setShowAddEntityForm(false);
      console.log('✅ Entity added successfully:', entity.scientificName);
    } catch (error) {
      console.error('❌ Error adding entity:', error);
    }
  };

  // Filter entities based on search and filters
  const getFilteredEntities = () => {
    let entities = biological_entities || [];
    
    // Debug logging for production
    console.log('🔍 IPC Setup - Biological Entities Debug:', {
      totalEntities: entities.length,
      searchQuery,
      filterCategory,
      filterPriority,
      entities: entities.slice(0, 3), // First 3 entities for debugging
      loadingEntities
    });
    
    // Filter by search query
    if (searchQuery) {
      entities = entities.filter(entity => 
        (entity.scientificName && entity.scientificName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (entity.commonName && entity.commonName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (entity.resistanceProfile && Array.isArray(entity.resistanceProfile) && entity.resistanceProfile.some(r => 
          r.toLowerCase().includes(searchQuery.toLowerCase())
        )) ||
        (entity.resistanceProfile && typeof entity.resistanceProfile === 'object' && !Array.isArray(entity.resistanceProfile) && 
          JSON.stringify(entity.resistanceProfile).toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Filter by category
    if (filterCategory) {
      entities = entities.filter(entity => entity.category === filterCategory);
    }
    
    // Filter by priority
    if (filterPriority) {
      entities = entities.filter(entity => entity.priority === filterPriority);
    }
    
    return entities;
  };

  const filteredEntities = getFilteredEntities();

  return (
    <div className="clinical-mapper">
      {/* Biological Entities Section */}
      {activeSection === 'ipc-biological-entities' && (
        <>
          {!orgLoading && !activeOrgId && (
            <div className="content-card" style={{ marginBottom: '12px' }}>
              <div className="card-content">
                <p role="alert" style={{ margin: 0 }}>
                  {t('selectOrganizationForBiologicalEntities') ||
                    'Selectați spitalul activ (din meniu) pentru a gestiona entitățile biologice ale organizației.'}
                </p>
              </div>
            </div>
          )}
          {/* Statistics */}
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">Pathogen Statistics</div>
            </div>
            <div className="card-content">
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '16px' 
              }}>
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748' }}>
                    {biological_entities?.length || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{t('totalEntities')}</div>
                </div>
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#e53e3e' }}>
                    {biological_entities?.filter(e => e.category === 'Bacterial').length || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{t('bacteria')}</div>
                </div>
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d69e2e' }}>
                    {biological_entities?.filter(e => e.category === 'Fungal').length || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{t('fungi')}</div>
                </div>
                <div style={{ 
                  padding: '16px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3182ce' }}>
                    {biological_entities?.filter(e => e.category === 'Viral').length || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096' }}>{t('viruses')}</div>
                </div>
              </div>
                </div>
              </div>

          {/* Add Entity Form - Inline at top */}
          <div className="content-card">
            <div className="card-header">
<div className="card-title">{t('addNewBiologicalEntity')}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAddEntityForm(!showAddEntityForm)}
                >
                  {showAddEntityForm ? t('cancel') : t('addEntity')}
                </button>
                {showAddEntityForm && (
                  <button 
                    className="btn btn-primary"
                    onClick={addEntity}
                    disabled={
                      !activeOrgId ||
                      !newEntity.scientificName ||
                      !newEntity.category ||
                      !newEntity.priority
                    }
                  >
                    {t('saveEntity')}
                  </button>
                )}
              </div>
            </div>
            
            {showAddEntityForm && (
              <div className="card-content">
                <EntityForm 
                  entity={newEntity}
                  setEntity={setNewEntity}
                  isNew={true}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                />
              </div>
            )}
              </div>

          {/* Entities List */}
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                {t('biologicalEntitiesCount', { count: loadingEntities ? '...' : filteredEntities.length })}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!activeOrgId) return;
                    setLoadingEntities(true);
                    try {
                      const entities = await biologicalEntityService.getWhere(
                        'organizationId',
                        '==',
                        activeOrgId
                      );
                      setBiologicalEntities(entities);
                    } finally {
                      setLoadingEntities(false);
                    }
                  }}
                  disabled={loadingEntities || !activeOrgId}
                >
                  {loadingEntities ? t('loading') : t('refresh')}
                </button>
              </div>
            </div>
            <div className="card-content">
              {loadingEntities ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>{t('loadingBiologicalEntities')}</p>
                </div>
              ) : (
                <>
              {/* Filters */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <input 
                  type="text" 
                  placeholder={t('searchEntities')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    padding: '10px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                  <select 
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('allCategories')}</option>
                  <option value="Bacteria">{t('bacteria')}</option>
                  <option value="Fungi">{t('fungi')}</option>
                  <option value="Viruses">{t('viruses')}</option>
                  </select>
                  <select 
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">{t('allPriorities')}</option>
                  <option value="Critical">{t('critical')}</option>
                  <option value="High">{t('high')}</option>
                  <option value="Medium">{t('medium')}</option>
                  <option value="Emerging">{t('emerging')}</option>
                  </select>
              </div>

              {/* Entity List */}
              <div className="entity-list">
                {filteredEntities.length === 0 ? (
                  <div className="empty-state">
                    <p>No biological entities found.</p>
                  </div>
                ) : (
                  filteredEntities.map(entity => (
                    <div key={entity.id} className="entity-card" style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                      {/* Entity Summary */}
                      <div 
                        className="entity-summary"
                        style={{ 
                          padding: '16px', 
                          backgroundColor: editingEntityId === entity.id ? '#f0f8ff' : '#ffffff',
                          cursor: 'pointer',
                          borderBottom: editingEntityId === entity.id ? '1px solid #e2e8f0' : 'none'
                        }}
                        onClick={() => {
                          if (editingEntityId === entity.id) {
                            setEditingEntityId(null);
                            setEditingEntity(null);
                          } else {
                            handleEditEntity(entity);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div className="entity-info">
                            <div className="entity-name" style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                          {entity.scientificName || entity.commonName || t('unnamedEntity')}
                        </div>
                        {entity.commonName && entity.commonName !== entity.scientificName && (
                              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                                {t('common')}: {entity.commonName}
                              </div>
                          )}
                            <div className="entity-details" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span className="status" style={{ 
                            backgroundColor: entity.priority === 'Critical' ? '#f56565' : 
                                           entity.priority === 'High' ? '#ed8936' :
                                           entity.priority === 'Medium' ? '#d69e2e' : '#718096',
                            color: '#ffffff',
                                fontWeight: '500',
                                padding: '1px 6px',
                                borderRadius: '8px',
                                fontSize: '10px'
                          }}>
                            {entity.priority || 'Unknown'}
                          </span>
                              <span style={{ 
                                backgroundColor: '#e2e8f0', 
                                padding: '1px 6px', 
                                borderRadius: '8px', 
                                fontSize: '10px' 
                              }}>
                                {entity.category || 'Unknown'}
                              </span>
                              {entity.rpnScore && (
                                <span style={{ 
                                  backgroundColor: '#805ad5', 
                                  color: 'white',
                                  padding: '1px 6px', 
                                  borderRadius: '8px', 
                                  fontSize: '10px' 
                                }}>
                                  RPN: {entity.rpnScore}
                                </span>
                              )}
                              {entity.whoPriorityTier && (
                                <span style={{ 
                                  backgroundColor: '#2b6cb0', 
                                  color: 'white',
                                  padding: '1px 6px', 
                                  borderRadius: '8px', 
                                  fontSize: '10px' 
                                }}>
                                  WHO: {entity.whoPriorityTier}
                                </span>
                              )}
                        </div>
                        {/* Common Sites */}
                        {entity.commonSites && entity.commonSites.length > 0 && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '13px', 
                            color: '#4a5568',
                            lineHeight: '1.5'
                          }}>
                                <strong style={{ color: '#2d3748' }}>{t('sites')}:</strong> {entity.commonSites.join(', ')}
                          </div>
                        )}
                        {/* Risk Factors */}
                        {entity.riskFactors && entity.riskFactors.length > 0 && (
                          <div style={{ 
                            marginTop: '4px', 
                            fontSize: '13px', 
                            color: '#4a5568',
                            lineHeight: '1.5'
                          }}>
                            <strong style={{ color: '#2d3748' }}>{t('riskFactors')}:</strong> {entity.riskFactors.join(', ')}
                          </div>
                        )}
                      </div>
                          <div className="entity-actions" style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEntity(entity);
                              }}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: editingEntityId === entity.id ? '#e53e3e' : '#805ad5',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {editingEntityId === entity.id ? t('cancel') : t('edit')}
                        </button>
                            {editingEntityId === entity.id && (
                  <button 
                                className="btn btn-save"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await saveEntityChanges();
                                  setEditingEntityId(null);
                      setEditingEntity(null);
                    }}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#38a169',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                {t('save')}
                  </button>
                            )}
                </div>
                    </div>
                    </div>

                      {/* Inline Edit Form */}
                      {editingEntityId === entity.id && (
                        <div className="entity-edit-form" style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                          <EntityForm 
                            entity={entity}
                            setEntity={setEditingEntity}
                            isNew={false}
                            entityId={entity.id}
                            updateNestedField={updateNestedField}
                            updateArrayField={updateArrayField}
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                          />
                        </div>
                      )}
                    </div>
                  ))
                )}
                    </div>
                </>
              )}
                  </div>
                </div>

        </>
      )}

      {activeSection === 'ipc-biocide' && <BiocideList />}

      {/* IC Leadership Section */}
      {activeSection === 'ipc-ic-leadership' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('infectionControlLeadership')}</div>
          </div>
          <div className="card-content">
            <div className="form-grid">
              <div className="form-field">
                <label>{t('infectionControlOfficer')}</label>
                <input
                  type="text"
                  value={state.icLeadership?.infectionControlOfficer || ''}
                  onChange={(e) => updateICLeadership('infectionControlOfficer', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('icOfficerEmail')}</label>
                <input
                  type="email"
                  value={state.icLeadership?.infectionControlEmail || ''}
                  onChange={(e) => updateICLeadership('infectionControlEmail', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('icOfficerPhone')}</label>
                <input
                  type="tel"
                  value={state.icLeadership?.infectionControlPhone || ''}
                  onChange={(e) => updateICLeadership('infectionControlPhone', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('medicalDirectorIC')}</label>
                <input
                  type="text"
                  value={state.icLeadership?.medicalDirectorIC || ''}
                  onChange={(e) => updateICLeadership('medicalDirectorIC', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('nurseManagerIC')}</label>
                <input
                  type="text"
                  value={state.icLeadership?.nurseManagerIC || ''}
                  onChange={(e) => updateICLeadership('nurseManagerIC', e.target.value)}
                />
              </div>

              <div className="form-field full-width">
                <label>{t('infectionControlPolicy')}</label>
                <textarea
                  value={state.icLeadership?.infectionControlPolicy || ''}
                  onChange={(e) => updateICLeadership('infectionControlPolicy', e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="form-actions">
              <button 
                className={`btn btn-primary ${saveSuccess ? 'save-success' : ''}`}
                onClick={() => {
                  console.log('Saving IC Leadership');
                  setSaveSuccess(true);
                  setTimeout(() => setSaveSuccess(false), 2000);
                }}
              >
                {saveSuccess ? (
                  <>
                    <span className="checkmark">✓</span> {t('saved')}
                  </>
                ) : (
                  t('saveChanges')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risk Management Section */}
      {activeSection === 'ipc-risk-management' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('riskManagement')}</div>
          </div>
          <div className="card-content">
            <div className="form-grid">
              <div className="form-field">
                <label>{t('riskAssessmentFrequency')}</label>
                <input
                  type="text"
                  value={state.riskManagement?.riskAssessmentFrequency || ''}
                  onChange={(e) => updateRiskManagement('riskAssessmentFrequency', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('baseRiskLevel')}</label>
                <select
                  value={state.riskManagement?.baseRiskLevel || ''}
                  onChange={(e) => updateRiskManagement('baseRiskLevel', e.target.value)}
                >
                  <option value="">{t('selectRiskLevel')}</option>
                  <option value="Low">{t('low')}</option>
                  <option value="Medium">{t('medium')}</option>
                  <option value="High">{t('high')}</option>
                  <option value="Critical">{t('critical')}</option>
                </select>
              </div>

              <div className="form-field">
                <label>{t('auditSchedule')}</label>
                <input
                  type="text"
                  value={state.riskManagement?.auditSchedule || ''}
                  onChange={(e) => updateRiskManagement('auditSchedule', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('reportingThreshold')}</label>
                <input
                  type="text"
                  value={state.riskManagement?.reportingThreshold || ''}
                  onChange={(e) => updateRiskManagement('reportingThreshold', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>{t('surveillanceFrequency')}</label>
                <input
                  type="text"
                  value={state.riskManagement?.surveillanceFrequency || ''}
                  onChange={(e) => updateRiskManagement('surveillanceFrequency', e.target.value)}
                />
              </div>
            </div>
            <div className="form-actions">
              <button 
                className={`btn btn-primary ${saveSuccess ? 'save-success' : ''}`}
                onClick={() => {
                  console.log('Saving Risk Management');
                  setSaveSuccess(true);
                  setTimeout(() => setSaveSuccess(false), 2000);
                }}
              >
                {saveSuccess ? (
                  <>
                    <span className="checkmark">✓</span> {t('saved')}
                  </>
                ) : (
                  t('saveChanges')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HAI Target Rates Section */}
      {activeSection === 'ipc-hai-targets' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('haiTargetRates')}</div>
            <div className="card-subtitle">{t('haiTargetsSubtitle')}</div>
          </div>
          <div className="card-content">
            <div className="info-section mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">📊 {t('haiMetricsOverview')}</h4>
              <p className="text-gray-600 mb-6">
                {t('haiMetricsDescription')}
              </p>
            </div>

            <div className="form-grid">
              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('cdiTargetRate')}</span>
                  <span className="text-xs text-gray-500">{t('per1000PatientDays')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.cdi || ''}
                  onChange={(e) => updateHAITargets('cdi', e.target.value)}
                  placeholder={t('placeholderCdi')}
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('cautiTargetRate')}</span>
                  <span className="text-xs text-gray-500">{t('per1000CatheterDays')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.cauti || ''}
                  onChange={(e) => updateHAITargets('cauti', e.target.value)}
                  placeholder={t('placeholderCauti')}
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('clabsiTargetRate')}</span>
                  <span className="text-xs text-gray-500">{t('per1000LineDays')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.clabsi || ''}
                  onChange={(e) => updateHAITargets('clabsi', e.target.value)}
                  placeholder={t('placeholderClabsi')}
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>SSI Target Rate</span>
                  <span className="text-xs text-gray-500">(per 100 procedures)</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.ssi || ''}
                  onChange={(e) => updateHAITargets('ssi', e.target.value)}
                  placeholder="e.g., 2.5%"
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('vapTargetRate')}</span>
                  <span className="text-xs text-gray-500">{t('per1000VentilatorDays')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.vap || ''}
                  onChange={(e) => updateHAITargets('vap', e.target.value)}
                  placeholder={t('placeholderVap')}
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('handHygieneTarget')}</span>
                  <span className="text-xs text-gray-500">{t('compliancePercent')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.handHygieneTarget || ''}
                  onChange={(e) => updateHAITargets('handHygieneTarget', e.target.value)}
                  placeholder={t('placeholderHandHygiene')}
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>Cleaning Compliance Target</span>
                  <span className="text-xs text-gray-500">(compliance %)</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.cleaningComplianceTarget || ''}
                  onChange={(e) => updateHAITargets('cleaningComplianceTarget', e.target.value)}
                  placeholder="e.g., 98%"
                />
              </div>

              <div className="form-field">
                <label className="flex items-center gap-2">
                  <span>{t('educationComplianceTarget')}</span>
                  <span className="text-xs text-gray-500">{t('compliancePercent')}</span>
                </label>
                <input
                  type="text"
                  value={state.haiTargetRates?.educationComplianceTarget || ''}
                  onChange={(e) => updateHAITargets('educationComplianceTarget', e.target.value)}
                  placeholder={t('placeholderEducation')}
                />
              </div>
            </div>
            <div className="form-actions">
              <button 
                className={`btn btn-primary ${saveSuccess ? 'save-success' : ''}`}
                onClick={() => {
                  console.log('Saving HAI Targets');
                  setSaveSuccess(true);
                  setTimeout(() => setSaveSuccess(false), 2000);
                }}
              >
                {saveSuccess ? (
                  <>
                    <span className="checkmark">✓</span> Saved
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
            </div>
          )}

      {/* Policy Framework Section */}
      {activeSection === 'ipc-policy-framework' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('policyFramework')}</div>
          </div>
          <div className="card-content">
            <div className="empty-state">
              <p>This section is under development.</p>
            </div>
          </div>
        </div>
      )}

      {/* Default/Fallback - Show welcome message if no section matches */}
      {!['ipc-ic-leadership', 'ipc-risk-management', 'ipc-policy-framework', 'ipc-hai-targets', 'ipc-biological-entities', 'ipc-biocide'].includes(activeSection) && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('infectionPreventionControl')}</div>
            </div>
          <div className="card-content">
            <div className="empty-state">
              <p>{t('selectSectionPrompt')}</p>
                </div>
                </div>
                </div>
      )}

                </div>
  );
};

// EntityForm Component for inline editing
const EntityForm = ({ entity, setEntity, isNew = false, entityId = null, updateNestedField, updateArrayField, expandedSections, toggleSection }) => {
  const { t } = useTranslation('ipc');
  const sections = [
    {
      id: 'identification',
      title: t('sectionIdentification'),
      fields: [
        { key: 'scientificName', label: t('entityScientificName'), type: 'text', required: true },
        { key: 'commonName', label: t('entityCommonName'), type: 'text' },
        { key: 'synonyms', label: t('entitySynonyms'), type: 'array' }
      ]
    },
    {
      id: 'classification',
      title: t('sectionClassification'),
      fields: [
        { key: 'category', label: t('entityCategory'), type: 'select', options: ['Bacterial', 'Viral', 'Fungal', 'Parasitic'], required: true },
        { key: 'classification', label: t('entityBactClassification'), type: 'select', options: ['Gram-negative', 'Gram-positive', 'Gram-variable', 'N/A'], required: false },
        { key: 'taxonomicFamily', label: t('entityTaxonomicFamily'), type: 'text' },
        { key: 'taxonomicOrder', label: t('entityTaxonomicOrder'), type: 'text' }
      ]
    },
    {
      id: 'whocdc',
      title: t('sectionWhocdc'),
      fields: [
        { key: 'whoPriorityTier', label: t('entityWhoPriorityTier'), type: 'select', options: ['Critical', 'High', 'Medium', ''] },
        { key: 'cdcNotifiable', label: t('entityCdcNotifiable'), type: 'checkbox' },
        { key: 'cdcCategory', label: t('entityCdcCategory'), type: 'text' },
        { key: 'associatedInfectionTypes', label: t('entityAssociatedInfectionTypes'), type: 'array' }
      ]
    },
    {
      id: 'transmission',
      title: t('sectionTransmission'),
      fields: [
        { key: 'transmissionRoutes', label: t('entityTransmissionRoutes'), type: 'array' },
        { key: 'primaryTransmissionRoute', label: t('entityPrimaryTransmissionRoute'), type: 'select', options: ['contact', 'droplet', 'airborne', 'bloodborne', 'vector-borne', 'fecal-oral', 'vertical'] },
        { key: 'typicalOnset', label: t('entityTypicalOnset'), type: 'select', options: ['Early-onset (<48h)', 'Late-onset (>48h)', 'Healthcare-associated', 'Community-acquired'] },
        { key: 'incubationPeriod.min', label: t('entityIncubationPeriodMin'), type: 'number' },
        { key: 'incubationPeriod.max', label: t('entityIncubationPeriodMax'), type: 'number' },
        { key: 'incubationPeriod.unit', label: t('entityIncubationPeriodUnit'), type: 'select', options: ['hours', 'days', 'weeks'] },
        { key: 'incubationPeriod.typical', label: t('entityTypicalIncubation'), type: 'number' },
        { key: 'incubationPeriod.notes', label: t('entityIncubationNotes'), type: 'text' }
      ]
    },
    {
      id: 'clinical',
      title: t('sectionClinical'),
      fields: [
        { key: 'commonSites', label: t('entityCommonSites'), type: 'array' },
        { key: 'riskFactors', label: t('entityRiskFactors'), type: 'array' },
        { key: 'clinicalPresentation.symptoms', label: t('entitySymptoms'), type: 'array' },
        { key: 'clinicalPresentation.severityRange', label: t('entitySeverityRange'), type: 'text' },
        { key: 'clinicalPresentation.typicalCourse', label: t('entityTypicalCourse'), type: 'text' },
        { key: 'clinicalPresentation.diagnosticCriteria', label: t('entityDiagnosticCriteria'), type: 'array' }
      ]
    },
    {
      id: 'risk',
      title: t('sectionRisk'),
      fields: [
        { key: 'severityScore', label: t('entitySeverityScore'), type: 'number', min: 1, max: 10 },
        { key: 'occurrenceProbability', label: t('entityOccurrenceProbability'), type: 'number', min: 1, max: 10 },
        { key: 'detectionDifficulty.score', label: t('entityDetectionDifficulty'), type: 'number', min: 1, max: 10 },
        { key: 'detectionDifficulty.timeToResults', label: t('entityTimeToResults'), type: 'text' },
        { key: 'detectionDifficulty.falseNegativeRisk', label: t('entityFalseNegativeRisk'), type: 'select', options: ['Low', 'Moderate', 'High'] },
        { key: 'rpnScore', label: t('entityRpnScore'), type: 'number', readonly: true },
        { key: 'priority', label: t('entityPriority'), type: 'select', options: ['Critical', 'High', 'Medium', 'Low'], required: true }
      ]
    },
    {
      id: 'resistance',
      title: t('sectionResistance'),
      fields: [
        { key: 'resistanceProfile.patterns', label: t('entityResistancePatterns'), type: 'array' },
        { key: 'resistanceProfile.concernLevel', label: t('entityConcernLevel'), type: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
        { key: 'resistanceProfile.geographicDistribution', label: t('entityGeographicDistribution'), type: 'text' },
        { key: 'vaccineAvailable', label: t('entityVaccineAvailable'), type: 'checkbox' }
      ]
    },
    {
      id: 'prevention',
      title: t('sectionPrevention'),
      fields: [
        { key: 'preventionStrategies.standard', label: t('entityStandardPrevention'), type: 'array' },
        { key: 'preventionStrategies.transmission', label: t('entityTransmissionPrevention'), type: 'array' },
        { key: 'preventionStrategies.isolation.type', label: t('entityIsolationType'), type: 'select', options: ['Standard', 'Contact', 'Droplet', 'Airborne', 'Protective'] },
        { key: 'preventionStrategies.environmentalControls.cleaningFrequency', label: t('entityCleaningFrequency'), type: 'text' },
        { key: 'preventionStrategies.environmentalControls.disinfectants', label: t('entityDisinfectants'), type: 'array' }
      ]
    },
    {
      id: 'detection',
      title: t('sectionDetection'),
      fields: [
        { key: 'detectionMethods', label: t('entityDetectionMethods'), type: 'array' },
        { key: 'confirmationRequired', label: t('entityConfirmationRequired'), type: 'checkbox' },
        { key: 'reportingRequirements.localHealthDept', label: t('entityReportToLocalHealthDept'), type: 'checkbox' },
        { key: 'reportingRequirements.nationalSurveillance', label: t('entityNationalSurveillance'), type: 'checkbox' },
        { key: 'reportingRequirements.timeframe', label: t('entityReportingTimeframe'), type: 'text' }
      ]
    },
    {
      id: 'references',
      title: t('sectionReferences'),
      fields: [
        { key: 'references', label: t('entityReferences'), type: 'array' },
        { key: 'educationalResources', label: t('entityEducationalResources'), type: 'array' }
      ]
    },
    {
      id: 'audit',
      title: t('sectionAudit'),
      fields: [
        { key: 'status', label: t('entityStatus'), type: 'select', options: ['Active', 'Under Review', 'Deprecated'] },
        { key: 'version', label: t('entityVersion'), type: 'number', readonly: true },
        { key: 'lastReviewedBy', label: t('entityLastReviewedBy'), type: 'text' },
        { key: 'reviewDueDate', label: t('entityReviewDueDate'), type: 'date' }
      ]
    }
  ];

  const updateField = (path, value) => {
    if (isNew) {
      // For new entities, update the state directly
      if (path.includes('.')) {
        const pathArray = path.split('.');
        setEntity(prev => {
          const newEntity = { ...prev };
          let current = newEntity;
          
          for (let i = 0; i < pathArray.length - 1; i++) {
            if (!current[pathArray[i]]) {
              current[pathArray[i]] = {};
            }
            current = current[pathArray[i]];
          }
          
          current[pathArray[pathArray.length - 1]] = value;
          
          // Auto-calculate RPN if risk scoring fields are updated
          if (path.includes('severityScore') || path.includes('occurrenceProbability') || path.includes('detectionDifficulty.score')) {
            const severity = newEntity.severityScore || 0;
            const occurrence = newEntity.occurrenceProbability || 0;
            const detection = newEntity.detectionDifficulty?.score || 0;
            newEntity.rpnScore = severity * occurrence * detection;
          }
          
          return newEntity;
        });
      } else {
        setEntity(prev => {
          const newEntity = { ...prev, [path]: value };
          
          // Auto-calculate RPN if risk scoring fields are updated
          if (path === 'severityScore' || path === 'occurrenceProbability') {
            const severity = newEntity.severityScore || 0;
            const occurrence = newEntity.occurrenceProbability || 0;
            const detection = newEntity.detectionDifficulty?.score || 0;
            newEntity.rpnScore = severity * occurrence * detection;
          }
          
          return newEntity;
        });
      }
    } else {
      // For existing entities, use the update functions
      if (path.includes('.')) {
        updateNestedField(entityId, path, value);
      } else {
        updateArrayField(entityId, path, value);
      }
    }
  };

  const getFieldValue = (path) => {
    if (path.includes('.')) {
      const pathArray = path.split('.');
      let value = entity;
      for (const key of pathArray) {
        value = value?.[key];
      }
      return value || '';
    }
    return entity[path] || '';
  };

  const renderField = (field) => {
    const value = getFieldValue(field.key);
    
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateField(field.key, e.target.value)}
            placeholder={field.label}
            required={field.required}
            readOnly={field.readonly}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: field.readonly ? '#f9fafb' : '#ffffff'
            }}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => updateField(field.key, parseInt(e.target.value) || '')}
            placeholder={field.label}
            min={field.min}
            max={field.max}
            required={field.required}
            readOnly={field.readonly}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: field.readonly ? '#f9fafb' : '#ffffff'
            }}
          />
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => updateField(field.key, e.target.value)}
            required={field.required}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#ffffff'
            }}
          >
            <option value="">{t('select')} {field.label}</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => updateField(field.key, e.target.checked)}
            style={{
              width: '16px',
              height: '16px',
              cursor: 'pointer'
            }}
          />
        );
      
      case 'array':
        return (
          <input
            type="text"
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={(e) => updateField(field.key, e.target.value)}
            placeholder={`${field.label} ${t('commaSeparated')}`}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#ffffff'
            }}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => updateField(field.key, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#ffffff'
            }}
          />
        );
      
      default:
        return (
          <input 
            type="text" 
            value={value} 
            onChange={(e) => updateField(field.key, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: '#ffffff'
            }}
          />
        );
    }
  };

  return (
    <div className="entity-form" style={{ backgroundColor: '#ffffff', padding: '20px' }}>
      {sections.map((section, sectionIndex) => (
        <div key={section.id} className="form-section">
          {/* Section Title */}
          <h3 style={{ 
            margin: sectionIndex === 0 ? '0 0 20px 0' : '40px 0 20px 0', 
            paddingBottom: '8px',
            borderBottom: '1px solid #e2e8f0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            {section.title}
          </h3>
          
          {/* Section Fields */}
          <div className="form-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '16px', 
            marginBottom: '20px' 
          }}>
            {section.fields.map(field => (
              <div key={field.key} className="form-field">
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontWeight: '500',
                  fontSize: '14px',
                  color: '#4a5568'
                }}>
                  {field.label}
                  {field.required && <span style={{ color: '#e53e3e', marginLeft: '4px' }}> *</span>}
                </label>
                {renderField(field)}
              </div>
            ))}
            </div>
        </div>
      ))}
    </div>
  );
};

export default IPCSetup;

